import { useRef, useState } from "react";
import { UploadSimple, ImageSquare, X } from "@phosphor-icons/react";
import { uploadsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function ImageUploader({ value, onChange, label, height = 200, testId = "image-uploader" }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef();

    const displayLabel = label !== undefined ? label : t("imageUploader.defaultLabel");

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error(t("imageUploader.toasts.selectImage"));
            return;
        }
        if (file.size > 6 * 1024 * 1024) {
            toast.error(t("imageUploader.toasts.maxSizeExceeded"));
            return;
        }
        setBusy(true);
        try {
            const uploaded = await uploadsApi.uploadObject(file);
            onChange?.(uploaded.url);
        } catch (e) {
            toast.error(t("imageUploader.toasts.failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            {displayLabel && <div className="overline mb-2">{displayLabel}</div>}
            <div
                className="relative rounded-2xl overflow-hidden group"
                style={{
                    background: "rgba(0,0,0,0.25)",
                    border: `1px ${dragging ? "solid" : "dashed"} ${dragging ? "var(--gold)" : "rgba(255,255,255,0.12)"}`,
                    minHeight: height,
                    transition: "border-color 0.3s ease, background 0.3s ease",
                }}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    handleFile(e.dataTransfer.files?.[0]);
                }}
                data-testid={testId}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />

                {value ? (
                    <>
                        <img src={value} alt="uploaded" className="w-full h-full object-cover" style={{ maxHeight: height * 1.6 }} />
                        <button
                            onClick={() => onChange?.(null)}
                            className="absolute top-3 right-3 w-8 h-8 rounded-full glass grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`${testId}-clear`}
                            aria-label={t("imageUploader.remove")}
                        >
                            <X size={12} />
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="w-full h-full flex flex-col items-center justify-center gap-3 py-8 text-white/60 hover:text-white transition-colors"
                        style={{ minHeight: height }}
                        data-testid={`${testId}-trigger`}
                    >
                        {busy ? (
                            <>
                                <UploadSimple size={24} className="animate-pulse" color="#D4AF37" />
                                <span className="text-sm">{t("imageUploader.uploading")}</span>
                            </>
                        ) : (
                            <>
                                <ImageSquare size={24} color="#D4AF37" />
                                <span className="text-sm">{t("imageUploader.dropText")}</span>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("imageUploader.maxSize")}</span>
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
